<template>
    <transition appear name="fade-transition">
        <v-autocomplete
            ref="input"
            v-model="inputValue"
            v-test-id
            name="search"
            :hide-details="_.isEmpty(errorMessagesValue) && _.isEmpty(rulesValue)"
            hide-no-data
            :label="labelValue"
            :items.sync="items"
            :item-text="itemText"
            :item-value="itemKey"
            :search-input.sync="search"
            :required="required"
            :rules="rulesValue"
            :error-messages="errorMessagesValue"
            :clearable="!required"
            :loading="searchLoading"
            :disabled="disabled"
            background-color="background"
            color="primaryText"
            item-color="primaryText"
            outlined
            dense
            :class="{ 'mb-7': _.isEmpty(rulesValue) }"
            open-on-clear
            return-object
            :multiple="multiple || allowMultiple"
            @keydown="onKeyDown"
            @blur="onBlur"
            @click:clear="onClear"
        >
            <template v-if="multiple || allowMultiple" #item="{ item }">
                {{ item[`${itemText}`] }}
            </template>
        </v-autocomplete>
    </transition>
</template>

<script>
import BaseField from './BaseField';
import LookupApi from '@/api/LookupApi';

export default {
    name: 'Lookup',
    extends: BaseField,
    props: {
        type: {
            type: String,
            required: true
        },
        multiple: {
            type: Boolean,
            default: false
        },
        returnObject: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        lookupSearchKey: 'name',
        itemKey: 'id',
        itemText: 'name',
        searchLoading: false,
        search: null,
        items: [],
        querySearchDebounced: () => {},
        initialResults: [],
        allowMultiple: false
    }),
    computed: {
        inputValue: {
            get() {
                return this.value;
            },
            set(value) {
                let returnId = null;
                let returnObject = null;

                if (this.multiple) {
                    returnId =
                        value.length > 0
                            ? value.map(item => (typeof item === 'string' ? item : item[`${this.itemKey}`]))
                            : null;
                    returnObject = value ? value : null;
                } else {
                    returnId = value ? value[`${this.itemKey}`] : null;
                    returnObject = value ? value : null;
                }

                value = this.returnObject ? returnObject : returnId;

                this.$emit('input', value);
                this.$emit('input:id', returnId);
                this.$emit('input:object', returnObject);
            }
        },
        searchIsValid() {
            return this.search && this.search.length > 0;
        }
    },
    watch: {
        search(v) {
            if (!this.searchIsValid) {
                this.resetItems();
                return;
            }

            if (v === this.inputValue) {
                return;
            }

            this.querySearchDebounced(v);
        }
    },
    async mounted() {
        await this.reset();
    },
    methods: {
        async reset() {
            this.querySearchDebounced = this._.debounce(this.querySearch, 500);
            await this.querySearch(null);
            this.$emit('load');
        },
        resetItems() {
            this.items = this.initialResults;
        },
        async querySearch(v) {
            this.searchLoading = true;

            try {
                const results = await new LookupApi().methods.search(this.type, v, this.lookupSearchKey);

                if (this._.isNull(v)) {
                    this.initialResults = results.data;
                }

                this.items = results.data;
            } catch (error) {
                //
            }

            this.searchLoading = false;
        },
        async createNewItem(v) {
            this.searchLoading = true;

            const match = this._.find(this.items, v => v[`${this.itemText}`] === this.search);

            if (match) {
                this.appendItemToValue(match[`${this.itemKey}`], match[`${this.itemText}`]);
            } else {
                try {
                    const result = await new LookupApi().methods.post(this.type, v);
                    await this.querySearch(null);
                    await this.querySearch(v);
                    this.appendItemToValue(result.data.created_id, v);
                } catch (error) {
                    this.handleError(error);
                }
            }

            this.search = '';

            this.searchLoading = false;
        },
        onKeyDown(e) {
            if (e.keyCode === 13 && this.searchIsValid) {
                this.createNewItem(this.search);
            }
        },
        onBlur() {
            if (this.searchIsValid) {
                this.createNewItem(this.search);
            }
        },
        onClear() {
            this.search = null;
            this.$emit('input', null);
            this.$emit('input:id', null);
            this.$emit('input:object', null);
        },
        appendItemToValue(id, name) {
            const item = {
                [`${this.itemKey}`]: id,
                [`${this.itemText}`]: name
            };

            let value;

            if (this.multiple || this.allowMultiple) {
                if (this.inputValue && Array.isArray(this.inputValue)) {
                    value = [...this.inputValue, item];
                } else {
                    value = [item];
                }
            } else {
                value = item;
            }

            this.inputValue = value;
        }
    }
};
</script>
